import { useEffect, useState, type FormEvent } from 'react'
import type { IsbtTemplate, IsbtTemplatePayload } from '@/api/labelPrintingApi'
import { useLabelPrintingMutations, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import {
  DATE_FORMAT_LABEL,
  ISBT_DATE_FORMATS,
  ISBT_USES,
  SIZE_TO_CONFIRM,
  USE_LABEL,
  canApproveIsbtTemplate,
  fileNameError,
  formatDay,
  isbtTemplateStatus,
  validateIsbtTemplate,
  type IsbtDateFormat,
  type IsbtTemplateDraft,
  type IsbtUse,
} from '@/lib/labelPrinting'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, StatusChip, SwitchField, activeNote } from './shared'

// Radix Select cannot hold an empty value, so "To confirm" (null) has its own.
const NO_SIZE = '__to_confirm__'

const DATE_EXAMPLE: Record<IsbtDateFormat, string> = {
  standard: '01 Oct 2026',
  mfg: '10 01 2026',
}

function draftOf(t: IsbtTemplate | undefined, firstSize: string | null): IsbtTemplateDraft {
  return t
    ? {
        displayName: t.displayName,
        fileName: t.fileName,
        labelSize: t.labelSize,
        use: t.use,
        dateFormat: t.dateFormat,
        active: t.active,
      }
    : { displayName: '', fileName: '', labelSize: firstSize, use: 'full', dateFormat: 'standard', active: true }
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

/** Add or edit an ISBT template, and approve a saved one. */
export function IsbtTemplateDialog({
  template,
  onClose,
  onDone,
}: {
  template?: IsbtTemplate
  onClose: () => void
  onDone: (message: string) => void
}) {
  const options = useLabelPrintingOptions()
  const sizes = options.data?.isbtSizes ?? []
  const uses = options.data?.uses ?? [...ISBT_USES]
  const dateFormats = options.data?.dateFormats ?? [...ISBT_DATE_FORMATS]
  const { createIsbtTemplate, updateIsbtTemplate, approveIsbtTemplate } = useLabelPrintingMutations()

  const [initial] = useState(() => draftOf(template, sizes[0] ?? null))
  const [draft, setDraft] = useState(initial)
  // "To confirm" is a real choice, so a new draft takes the first size only
  // while the user has not picked one.
  const [sizePicked, setSizePicked] = useState(false)
  const firstSize = sizes[0]
  useEffect(() => {
    if (!template && !sizePicked && firstSize !== undefined) {
      setDraft(d => (d.labelSize === null ? { ...d, labelSize: firstSize } : d))
    }
  }, [template, sizePicked, firstSize])
  // All errors show after a submit attempt; the file name check is live.
  const [touched, setTouched] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)


  const allErrors = validateIsbtTemplate(draft)
  const fileError = touched || draft.fileName ? fileNameError(draft.fileName) : null
  const nameError = touched ? allErrors.displayName : undefined

  // Approval belongs to the file that was test-printed (requirement 10).
  const locked = !!template?.approvedAt
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial)
  const pending = createIsbtTemplate.isPending || updateIsbtTemplate.isPending || approveIsbtTemplate.isPending

  const set = <K extends keyof IsbtTemplateDraft>(key: K, value: IsbtTemplateDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }))

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(allErrors).length > 0) return
    setServerError(null)
    const name = draft.displayName.trim()
    const base: IsbtTemplatePayload = {
      displayName: name,
      fileName: draft.fileName,
      labelSize: draft.labelSize,
      use: draft.use,
      dateFormat: draft.dateFormat,
    }
    const done = {
      onSuccess: () => onDone(`Saved “${name}”.`),
      onError: (err: unknown) => setServerError(errorText(err, 'Could not save the template.')),
    }
    if (!template) {
      createIsbtTemplate.mutate(base, done)
      return
    }
    // An approved template's file name and size are not sent: they cannot change.
    const { fileName, labelSize, ...rest } = base
    const payload = locked ? { ...rest, active: draft.active } : { ...rest, fileName, labelSize, active: draft.active }
    updateIsbtTemplate.mutate({ id: template.id, payload }, done)
  }

  function approve() {
    if (!template) return
    setServerError(null)
    approveIsbtTemplate.mutate(template.id, {
      onSuccess: () => onDone(`Approved “${template.displayName}”.`),
      onError: err => setServerError(errorText(err, 'Could not approve the template.')),
    })
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{template ? 'Edit ISBT template' : 'New ISBT template'}</DialogTitle>
          <DialogDescription>
            Smart CGT stores the file name only. The layout stays in the label program on the print computer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className='flex flex-col gap-4'>
          <Field id='isbt-template-name' label='Display name' help='What the user sees when printing.' error={nameError}>
            <Input
              id='isbt-template-name'
              value={draft.displayName}
              autoFocus
              autoComplete='off'
              aria-invalid={!!nameError}
              aria-describedby={nameError ? 'isbt-template-name-error' : undefined}
              onChange={e => set('displayName', e.target.value)}
            />
          </Field>

          <Field
            id='isbt-template-file'
            label='File name'
            help='Must match the .lbl file on the print computer exactly. No file is uploaded.'
            error={fileError ?? undefined}
          >
            <Input
              id='isbt-template-file'
              value={draft.fileName}
              disabled={locked}
              autoComplete='off'
              spellCheck={false}
              aria-invalid={!!fileError}
              aria-describedby={fileError ? 'isbt-template-file-error' : undefined}
              onChange={e => set('fileName', e.target.value)}
            />
          </Field>

          <div className='flex gap-3'>
            <Field id='isbt-template-size' label='Label size'>
              <Select
                value={draft.labelSize ?? NO_SIZE}
                disabled={locked}
                onValueChange={v => {
                  setSizePicked(true)
                  set('labelSize', v === NO_SIZE ? null : v)
                }}
              >
                <SelectTrigger id='isbt-template-size'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map(s => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                  <SelectItem value={NO_SIZE}>{SIZE_TO_CONFIRM}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field id='isbt-template-use' label='Use'>
              <Select value={draft.use} onValueChange={v => set('use', v as IsbtUse)}>
                <SelectTrigger id='isbt-template-use'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uses.map(u => (
                    <SelectItem key={u} value={u}>
                      {USE_LABEL[u]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {locked && (
            <p className='-mt-2 text-xs text-muted-foreground'>
              Approved templates keep their file and size. Retire it and add a new one to change them.
            </p>
          )}

          <fieldset className='flex flex-col gap-2'>
            <legend className='mb-1.5 text-sm font-medium'>Date format</legend>
            <div className='flex flex-wrap gap-4'>
              {dateFormats.map(f => (
                <label key={f} className='flex items-center gap-2 text-sm'>
                  <input
                    type='radio'
                    name='isbt-template-date'
                    value={f}
                    checked={draft.dateFormat === f}
                    onChange={() => set('dateFormat', f)}
                    className='h-4 w-4 accent-primary'
                  />
                  {DATE_FORMAT_LABEL[f]} ({DATE_EXAMPLE[f]})
                </label>
              ))}
            </div>
          </fieldset>

          {/* ISBT layouts live in the .lbl file on the print computer; Smart CGT never holds them, so there is nothing to draw. */}
          <section className='flex flex-col gap-1 rounded-lg border border-dashed p-3'>
            <h4 className='text-sm font-medium'>Preview</h4>
            <p className='text-sm text-muted-foreground'>
              No preview available. The label layout is set up on the print computer, not in Smart CGT.
            </p>
          </section>

          {template && (
            <div className='flex flex-col gap-2 rounded-lg border p-3'>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-sm font-medium'>Approval</span>
                <StatusChip {...isbtTemplateStatus(template)} />
              </div>
              <p className='text-sm'>
                {template.approvedAt
                  ? `Approved by ${template.approvedBy ?? 'unknown'} on ${formatDay(template.approvedAt)}`
                  : 'Awaiting test print and approval'}
              </p>
              {!template.approvedAt && (
                <p className='text-xs text-muted-foreground'>
                  Print a test label from an ISBT printer, check it, then approve it here.
                  {template.labelSize === null && ' A template with no label size cannot be approved.'}
                </p>
              )}
              {canApproveIsbtTemplate(template) && (
                <div className='flex items-center gap-3'>
                  <Button type='button' variant='outline' disabled={pending || dirty} onClick={approve}>
                    {approveIsbtTemplate.isPending ? 'Approving…' : 'Approve'}
                  </Button>
                  {dirty && <span className='text-xs text-muted-foreground'>Save your changes first.</span>}
                </div>
              )}
            </div>
          )}

          {template && (
            <SwitchField
              id='isbt-template-active'
              label='Active'
              checked={draft.active}
              onCheckedChange={v => set('active', v)}
              note={activeNote(draft.active)}
            />
          )}

          {serverError && (
            <p role='alert' className='text-sm text-destructive'>
              {serverError}
            </p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending}>
              {createIsbtTemplate.isPending || updateIsbtTemplate.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
